import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TurkishLeagueStatsBoard from './TurkishLeagueStatsBoard.jsx';
import banner1 from "./assets/banners/banner1.jpg";
import banner2 from "./assets/banners/banner2.jpg";
import banner3 from "./assets/banners/banner3.jpg";
import banner4 from "./assets/banners/banner4.jpg";
import extra from "./assets/icons/extra.png";
import fight from "./assets/icons/fight.png";
import nba from "./assets/icons/nba.png";
import originals from "./assets/icons/originals.png";
import racing from "./assets/icons/racing.png";
import BentoGridDemo from './components/ui/bento-grid-demo';
import EditorialStatement from './components/home/EditorialStatement';
import SpotlightRail from './components/home/SpotlightRail';

const channels = [
    { name: "DigitaleXtra", image: extra },
    { name: "Fight Digitale", image: fight },
    { name: "NBA Digitale", image: nba },
    { name: "Digitale Originals", image: originals },
    { name: "Digitale Racing", image: racing },
];

const banners = [banner1, banner2, banner3, banner4];

const blogPreview = [
    {
        title: "Bir Gol En Fazla Neye Mal Olabilir?",
        excerpt: "Andres Escobar, futbolun hem güzelliklerini hem de tehlikelerinin örneklerinden biridir.",
        readTime: "8 dk",
    },
    {
        title: "Manchester City Analizi",
        excerpt: "2022-2023 Premier Lig sezonu başladığında kimse böyle bir final beklemiyordu.",
        readTime: "12 dk",
    },
    {
        title: "Fenerbahçe'nin Sarı-Lacivert Hikâyesi",
        excerpt: "Fenerbahçe Spor Kulübü'nün renkleri arkasında yatan tarihi hikaye.",
        readTime: "10 dk",
    },
];

const Home = () => {
    const [currentBanner, setCurrentBanner] = useState(0);
    const [imagesLoaded, setImagesLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const preloadImages = async () => {
            await Promise.all(
                banners.map(
                    (src) =>
                        new Promise((resolve) => {
                            const img = new Image();
                            img.onload = resolve;
                            img.onerror = resolve;
                            img.src = src;
                        })
                )
            );
            if (!cancelled) setImagesLoaded(true);
        };
        preloadImages();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!imagesLoaded) return;
        const interval = setInterval(() => {
            setCurrentBanner((prev) => (prev + 1) % banners.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [imagesLoaded]);

    return (
        <div className="bg-[var(--bg)]">
            <div
                className="bg-[var(--ink)] text-white"
                style={{ paddingTop: 'var(--site-header-h, 3.5rem)' }}
            >
                <section className="relative w-full h-[78vh] min-h-[480px] max-h-[780px] overflow-hidden">
                    {imagesLoaded ? (
                        <img
                            src={banners[currentBanner]}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover scale-[1.02]"
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                            <div className="h-8 w-8 border border-white/40 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-black/20" />

                    <div className="relative z-10 h-full w-11/12 max-w-7xl mx-auto flex flex-col justify-end pb-28 md:pb-32">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="max-w-2xl text-white"
                        >
                            <p className="text-[11px] uppercase tracking-[0.28em] text-white/70 mb-3">
                                Dijital spor kanalı
                            </p>
                            <p className="font-display text-4xl md:text-6xl tracking-tight">Sports Digitale</p>
                            <p className="mt-3 text-sm md:text-base text-white/85 max-w-md leading-relaxed">
                                Canlı skorlar, özgün içerikler ve lig istatistikleri ile sporun nabzını tutun.
                            </p>
                        </motion.div>
                    </div>

                    <button
                        type="button"
                        aria-label="Önceki banner"
                        onClick={() => setCurrentBanner((currentBanner - 1 + banners.length) % banners.length)}
                        className="absolute left-2 md:left-5 top-[42%] z-20 h-12 w-12 flex items-center justify-center text-white/80 hover:text-white text-3xl leading-none"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        aria-label="Sonraki banner"
                        onClick={() => setCurrentBanner((currentBanner + 1) % banners.length)}
                        className="absolute right-2 md:right-5 top-[42%] z-20 h-12 w-12 flex items-center justify-center text-white/80 hover:text-white text-3xl leading-none"
                    >
                        ›
                    </button>

                    <div className="absolute bottom-[5.75rem] left-1/2 z-20 -translate-x-1/2 flex items-center gap-2">
                        {banners.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                aria-label={`Banner ${index + 1}`}
                                onClick={() => setCurrentBanner(index)}
                                className={`h-[2px] transition-all ${
                                    index === currentBanner ? 'w-8 bg-white' : 'w-3 bg-white/40 hover:bg-white/70'
                                }`}
                            />
                        ))}
                    </div>

                    <div className="absolute bottom-0 inset-x-0 z-20 border-t border-white/10 bg-black/55 backdrop-blur-[6px]">
                        <div className="w-11/12 max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-5">
                            {channels.map((channel, i) => (
                                <div
                                    key={channel.name}
                                    className={`group flex flex-col items-center justify-center gap-2 py-4 md:py-5 px-2 ${
                                        i < channels.length - 1 ? 'sm:border-r border-white/10' : ''
                                    }`}
                                >
                                    <img
                                        src={channel.image}
                                        alt=""
                                        className="h-9 w-9 md:h-11 md:w-11 object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                                    />
                                    <span className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-white/75 group-hover:text-white text-center">
                                        {channel.name}
                                    </span>
                                    <span className="h-px w-0 bg-[var(--accent)] transition-all duration-300 group-hover:w-8" />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            <EditorialStatement />

            <SpotlightRail />

            <section id="videos" className="w-11/12 max-w-7xl mx-auto py-16">
                <div className="mb-8 max-w-xl">
                    <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)]">Son YouTube Videoları</h2>
                    <p className="mt-2 text-sm text-[var(--muted)]">
                        Sports Digitale kanalından en yeni yayınlar.
                    </p>
                </div>
                <BentoGridDemo />
            </section>

            <section className="border-t border-[var(--line)] bg-[var(--surface)]">
                <div className="w-11/12 max-w-7xl mx-auto py-16 flex flex-col lg:flex-row gap-12 lg:gap-16">
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)] mb-8">Önemli Yazılar</h2>
                        <div className="divide-y divide-[var(--line)]">
                            {blogPreview.map((post) => (
                                <article key={post.title} className="py-6 first:pt-0">
                                    <Link to="/blog" className="group block">
                                        <h3 className="text-lg font-semibold text-[var(--ink)] group-hover:text-[var(--accent)] transition-colors">
                                            {post.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-[var(--muted)] leading-relaxed">
                                            {post.excerpt}
                                        </p>
                                        <span className="mt-3 inline-block text-xs text-[var(--muted)]">
                                            {post.readTime} okuma
                                        </span>
                                    </Link>
                                </article>
                            ))}
                        </div>
                    </div>
                    <div id="istatistik" className="w-full lg:max-w-md xl:max-w-lg shrink-0">
                        <TurkishLeagueStatsBoard />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
